import React from 'react';
import { Clock, Shield, RefreshCw, AlertCircle, CheckCircle, Package, CreditCard, Phone, Mail } from 'lucide-react';

export default function ReturnPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-black text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Return & Refund Policy
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Your satisfaction is our priority. We believe in making returns and refunds as simple and transparent as possible.
          </p>
        </div>
      </div>

      {/* Quick Overview Cards */}
      <div className="max-w-6xl mx-auto px-6 -mt-8">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-r-4 border-black">
            <Clock className="w-10 h-10 text-black mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">2-3 Days Window</h3>
            <p className="text-gray-600">Request returns within 2-3 days of receiving your order</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-r-4 border-black">
            <RefreshCw className="w-10 h-10 text-black mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Easy Process</h3>
            <p className="text-gray-600">Simple return process with dedicated customer support</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-r-4 border-black">
            <CreditCard className="w-10 h-10 text-black mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Quick Refunds</h3>
            <p className="text-gray-600">Approved refunds processed within 3-4 business days</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          
          {/* Policy Header */}
          <div className="bg-black text-white p-8">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Our Commitment</h2>
                <p className="text-gray-300 mt-1">Last updated: July 1, 2025</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-10">
            
            {/* Introduction */}
            <div>
              <p className="text-lg text-gray-700 text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">
                At <strong>VESHH-EON</strong>, we believe in helping our customers as far as possible. 
                Our liberal return and refund policy is designed to ensure your complete satisfaction with every purchase.
              </p>
            </div>

            {/* Cancellation Policy */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Cancellation Policy</h3>
              </div>
              
              <div className="space-y-4 pl-11">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-black mt-0.5" />
                  <p className="text-gray-700 text-justify">
                    <strong>Timing:</strong> Cancellations will be considered only if requested within <strong>2-3 days of placing the order</strong>.
                  </p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-black mt-0.5" />
                  <p className="text-gray-700 text-lg text-gray-700 text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">
                    <strong>Processing Status:</strong> Cancellation requests may not be entertained if orders have been communicated to vendors/merchants and shipping has been initiated.
                  </p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-black mt-0.5" />
                  <p className="text-gray-700 text-justify">
                    <strong>  Refund/replacement is available if product quality is compromised. </strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Return Policy */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Return Policy</h3>
              </div>
              
              <div className="space-y-6 pl-11">
                
                {/* Damaged/Defective Items */}
                <div className="bg-red-50 border border-black rounded-lg p-4">
                  <h4 className="font-bold text-black mb-2">Damaged or Defective Items</h4>
                  <ul className="space-y-2 text-black">
                    <li className="flex items-start space-x-2">
                      <span>•</span>
                      <span className="text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">Report damaged or defective items to our Customer Service team immediately</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span>•</span>
                      <span className="text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">Must be reported within <strong>2-3 days of receipt</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span>•</span>
                      <span className=" text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">Request will be processed after merchant verification</span>
                    </li>
                  </ul>
                </div>

                {/* Product Not As Expected */}
                <div className="bg-amber-50 border border-black rounded-lg p-4">
                  <h4 className="font-bold text-black mb-2">Product Not As Expected</h4>
                  <ul className="space-y-2 text-black">
                    <li className="flex items-start space-x-2">
                      <span>•</span>
                      <span className="text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">If the product is not as shown on the site or doesn't meet expectations</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span>•</span>
                      <span className="text-justify">Notify our customer service within <strong>2-3 days of receiving</strong> the product</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span>•</span>
                      <span className="text-justify">Our team will review and take appropriate action</span>
                    </li>
                  </ul>
                </div>

                {/* Warranty Items */}
{/*                 <div className="bg-purple-50 border border-black rounded-lg p-4">
                  <h4 className="font-bold text-black mb-2">Warranty Items</h4>
                  <p className="text-black">
                    For products with manufacturer warranty, please contact the manufacturer directly for warranty-related issues.
                  </p>
                </div> */}
              </div>
            </div>

            {/* Refund Process */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">
                  <CreditCard className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-black">Refund Process</h3>
              </div>
              
              <div className="pl-11">
                <div className="bg-green-50 border border-black rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-black" />
                    <h4 className="font-bold text-black">Processing Time</h4>
                  </div>
                  <p className="text-black text-lg text-justify">
                    Once your refund is approved by VESHH-EON, it will take <strong>3-4 business days</strong> for the refund to be processed to your original payment method.
                  </p>
                </div>
              </div>
            </div>

            {/* How to Request Return */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-black">How to Request a Return</h3>
              </div>
              
              <div className="pl-11 space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold p-4">1</div>
                  <p className="text-gray-700 pt-1 text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">Contact our Customer Service team within 2-3 days of receiving your order</p>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold p-4">2</div>
                  <p className="text-gray-700 pt-1 text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">Provide your order details and reason for return</p>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold p-4">3</div>
                  <p className="text-gray-700 pt-1 text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">Our team will review your request and provide further instructions</p>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold p-4">4</div>
                  <p className="text-gray-700 pt-1 text-justify leading-relaxed mb-4 hyphens-auto
        style={{ textJustify: 'inter-word' }}">Once approved, your refund will be processed within 3-4 business days</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Help with Your Return?</h3>
            <p className="text-gray-600">Our customer service team is here to assist you</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-black" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Email Support</h4>
                <p className="text-gray-600">support@veshheon.in</p>
                <p className="text-sm text-gray-500">Response within 24 hours</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-black" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Phone Support</h4>
                <p className="text-gray-600">+91 9480743215</p>
                <p className="text-sm text-gray-500">Mon-Fri, 9 AM - 6 PM IST</p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="mt-12 bg-black text-white rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-6">
            <AlertCircle className="w-8 h-8 text-white" />
            <h3 className="text-2xl font-bold">Important Notes</h3>
          </div>
          
          <div className="space-y-3">
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span className="text-justify">All return requests are subject to verification and approval by our team</span>
            </p>
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span className="text-justify">Products must be in original condition with all tags and packaging intact</span>
            </p>
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span className="text-justify">Custom or personalized items may not be eligible for returns</span>
            </p>
            <p className="flex items-start space-x-2">
              <span className="text-white">•</span>
              <span className="text-justify">This policy is subject to change. Please check for updates regularly</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
